// ANGEL FLIGHT — gun catalog + pure resolve helpers (first-person).
//
// Slice ships the minigun; autocannon/railgun/flamer are reserved so the Armory
// + save schema stay stable. Pure — no Phaser.

import { BALANCE } from './balance';
import type { PlayerStats, WeaponDef, WeaponId } from '../core/types';

export const WEAPONS: Readonly<Record<WeaponId, WeaponDef>> = Object.freeze({
  // The bread-and-butter hose: fast, single-target, thin tracers, a spin-up
  // before it reaches full rate. No special selection — one nearest hit/shot.
  minigun: {
    id: 'minigun',
    name: 'M134 Minigun',
    damage: 11,
    fireRate: 17,
    spreadPx: 26,
    hitRadiusPx: 30,
    ammo: 140,
    reloadMs: 1500,
    rangeZ: 60,
    tracerStyle: 'thin',
    recoilScale: 1,
    spinupMs: 350,
  },
  // Slow, heavy thump: high single-shot damage + a small explosive splash that
  // also bites enemies within blastRadiusPx of the primary hit. Fat tracer,
  // hefty recoil, no spin-up.
  autocannon: {
    id: 'autocannon',
    name: 'Autocannon',
    damage: 34,
    fireRate: 5,
    spreadPx: 10,
    hitRadiusPx: 34,
    ammo: 40,
    reloadMs: 1700,
    rangeZ: 60,
    blastRadiusPx: 90,
    // The shell explodes AT the primary's depth: splash only reaches enemies
    // within this many metres of it in world depth (screen px alone would let a
    // horizon shot blast the whole battlefield band).
    blastDepthM: 5,
    tracerStyle: 'fat',
    recoilScale: 2.2,
  },
  // Lance: pierces EVERY enemy along the muzzle→aim line, massive per-hit
  // damage, pinpoint (no spread), very slow, beam tracer, brutal recoil.
  railgun: {
    id: 'railgun',
    name: 'Railgun',
    damage: 120,
    fireRate: 1.2,
    spreadPx: 0,
    hitRadiusPx: 26,
    ammo: 12,
    reloadMs: 1600,
    rangeZ: 60,
    pierce: true,
    tracerStyle: 'beam',
    recoilScale: 3,
  },
  // Wide short cone: low per-tick damage but splashes EVERY enemy within
  // coneSpreadPx of the aim point each shot — IF it is within rangeZ metres
  // (enforced: beyond 18m the flames fall short, so the "short-range" identity
  // holds and the cone can never out-snipe the railgun at the horizon).
  // Flame tracer, almost no recoil.
  flamer: {
    id: 'flamer',
    name: 'Incinerator',
    damage: 5,
    fireRate: 22,
    spreadPx: 60,
    hitRadiusPx: 48,
    ammo: 200,
    reloadMs: 1400,
    rangeZ: 18,
    coneSpreadPx: 150,
    tracerStyle: 'flame',
    recoilScale: 0.4,
  },
} as const);

export function getWeapon(id: WeaponId): WeaponDef {
  return WEAPONS[id];
}

/** SCRIP price to unlock a weapon in the Armory (0 = the free starter). */
export function weaponPrice(id: WeaponId): number {
  return BALANCE.armory.weaponPrices[id];
}

/** Weapon-slot order: slot N (key/button "N") selects WEAPON_SLOTS[N-1]. The
 *  ONE source for the keyboard map, the touch buttons, the HUD slot hint, and
 *  the Armory listing — so every surface agrees which digit is which gun. */
export const WEAPON_SLOTS: readonly WeaponId[] = [
  'minigun',
  'autocannon',
  'railgun',
  'flamer',
] as const;

export function fireIntervalMs(def: WeaponDef, stats: PlayerStats): number {
  return 1000 / (def.fireRate * Math.max(0.1, stats.fireRateMult));
}

export function shotDamage(def: WeaponDef, stats: PlayerStats): number {
  return def.damage * Math.max(0, stats.damageMult);
}

export function effectiveAmmo(def: WeaponDef, stats: PlayerStats): number {
  return def.ammo + Math.max(0, Math.round(stats.ammoCap));
}

export function reloadDurationMs(def: WeaponDef, stats: PlayerStats): number {
  return def.reloadMs * Math.max(0.2, stats.reloadMult);
}

/* ──────────────────────────────────────────────────────────────────────────
 * Screen-space selection geometry (pure) — backs the AoE / pierce / cone
 * targeting in FiringSystem. Kept here, pure + unit-tested, so the multi-target
 * weapon identities are verifiable without booting the renderer.
 * ──────────────────────────────────────────────────────────────────────── */

/** Squared screen-px distance between two points (cheaper than the root). */
export function dist2(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

/** True if (px,py) lies within `radius` screen px of (cx,cy). The flamer cone
 *  and the autocannon blast both select with this. */
export function withinRadiusPx(
  px: number,
  py: number,
  cx: number,
  cy: number,
  radius: number,
): boolean {
  if (radius <= 0) return false;
  return dist2(px, py, cx, cy) <= radius * radius;
}

/** Perpendicular screen-px distance from point (px,py) to the line SEGMENT
 *  a→b (muzzle→aim). Used by the railgun to pick every enemy near the shot
 *  line. Degenerate (a≈b) segment falls back to point distance. */
export function distToSegmentPx(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const len2 = abx * abx + aby * aby;
  if (len2 <= 1e-6) return Math.sqrt(dist2(px, py, ax, ay));
  // Project P onto AB, clamped to the segment, then measure to the foot.
  let t = ((px - ax) * abx + (py - ay) * aby) / len2;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const fx = ax + abx * t;
  const fy = ay + aby * t;
  return Math.sqrt(dist2(px, py, fx, fy));
}
